import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { FileUploadModule } from 'primeng/fileupload';
import { DatePickerModule } from 'primeng/datepicker';
import { AdminImageSrcPipe } from '../../shared/pipes/admin-image-src.pipe';
export interface Update {
  id?: string;
  title: string;
  text: string;
  publishDate: Date;
  coverImage: File | string | null;
  coverImagePreview?: string;
}

@Component({
  selector: 'app-update-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonModule,
    InputTextModule,
    MessageModule,
    FileUploadModule,
    DatePickerModule,
    AdminImageSrcPipe,
  ],
  templateUrl: './update-dialog.component.html',
  styleUrl: './update-dialog.component.css',
})
export class UpdateDialogComponent implements OnInit {
  isEdit: boolean = false;
  coverImagePreview: string | null = null;
  projectId: string | null = null;

  // Form controls
  titleControl = new FormControl('', [Validators.required, Validators.minLength(3)]);
  textControl = new FormControl('', [Validators.required, Validators.minLength(10)]);
  publishDateControl = new FormControl<Date | null>(null, [Validators.required]);
  coverImageControl = new FormControl<File | null>(null);

  updateForm: FormGroup;

  constructor(
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private translateService: TranslateService
  ) {
    this.updateForm = new FormGroup({
      title: this.titleControl,
      text: this.textControl,
      publishDate: this.publishDateControl,
      coverImage: this.coverImageControl,
    });
  }

  ngOnInit(): void {
    // Check if we're editing an existing update
    if (this.config.data?.update) {
      this.isEdit = true;
      const update: Update = this.config.data.update;

      this.titleControl.setValue(update.title);
      this.textControl.setValue(update.text);
      this.publishDateControl.setValue(update.publishDate);

      if (update.coverImagePreview) {
        this.coverImagePreview = update.coverImagePreview;
      }
    } else {
      // Set default publish date to today for new updates
      this.publishDateControl.setValue(new Date());
    }

    // Capture projectId for image streaming URLs
    if (this.config.data?.projectId) {
      this.projectId = this.config.data.projectId as string;
    }
  }

  onCoverImageSelect(event: any): void {
    const file = event.files[0];
    if (file) {
      this.coverImageControl.setValue(file);

      // Create preview URL
      if (this.coverImagePreview && this.coverImagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(this.coverImagePreview);
      }
      this.coverImagePreview = URL.createObjectURL(file);
    }
  }

  removeCoverImage(): void {
    if (this.coverImagePreview && this.coverImagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(this.coverImagePreview);
    }
    this.coverImagePreview = null;
    this.coverImageControl.setValue(null);
  }

  private markFormGroupTouched(): void {
    this.titleControl.markAsTouched();
    this.textControl.markAsTouched();
    this.publishDateControl.markAsTouched();
  }

  onSubmit(): void {
    if (this.updateForm.invalid) {
      this.markFormGroupTouched();
      return;
    }

    const updateData: Update = {
      title: this.titleControl.value!,
      text: this.textControl.value!,
      publishDate: this.publishDateControl.value!,
      coverImage: this.coverImageControl.value,
      coverImagePreview: this.coverImagePreview || undefined,
    };

    if (this.isEdit && this.config.data?.update?.id) {
      updateData.id = this.config.data.update.id;
    }

    this.ref.close(updateData);
  }

  onCancel(): void {
    this.ref.close();
  }

  // Error message methods
  getTitleErrorMessage(): string {
    if (this.titleControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.errors.updateTitleRequired');
    }
    if (this.titleControl.errors?.['minlength']) {
      return this.translateService.instant('projectEdit.errors.updateTitleMinLength');
    }
    return '';
  }

  getTextErrorMessage(): string {
    if (this.textControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.errors.updateTextRequired');
    }
    if (this.textControl.errors?.['minlength']) {
      return this.translateService.instant('projectEdit.errors.updateTextMinLength');
    }
    return '';
  }

  getPublishDateErrorMessage(): string {
    if (this.publishDateControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.errors.updatePublishDateRequired');
    }
    return '';
  }
}
